import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * Theme token probe.
 *
 * A developer reference page, not product UI. It answers one question that is
 * otherwise tedious to check by hand: for each of the 38 design tokens
 * tailwind.config.js maps utilities onto, is the value coming from the ORG's
 * stored theme at runtime, or falling back to the static default in index.css?
 *
 * That distinction is the whole white-label story. A token showing "static" is a
 * token a customer cannot brand.
 *
 * It is also the first real TanStack route in the app — it takes precedence over
 * the catch-all in routes/$.tsx, so it doubles as a live check that the strangler
 * seam routes correctly.
 */

/** The tokens tailwind.config.js maps utility classes onto, grouped as tweakcn does. */
const GROUPS: { label: string; tokens: [string, string][] }[] = [
  { label: "Base", tokens: [["--background", "--foreground"]] },
  { label: "Primary", tokens: [["--primary", "--primary-foreground"]] },
  { label: "Secondary", tokens: [["--secondary", "--secondary-foreground"]] },
  { label: "Accent", tokens: [["--accent", "--accent-foreground"]] },
  { label: "Muted", tokens: [["--muted", "--muted-foreground"]] },
  { label: "Card", tokens: [["--card", "--card-foreground"]] },
  { label: "Popover", tokens: [["--popover", "--popover-foreground"]] },
  { label: "Destructive", tokens: [["--destructive", "--destructive-foreground"]] },
  { label: "Border & Input", tokens: [["--border", "--input"], ["--ring", ""]] },
  {
    label: "Sidebar",
    tokens: [
      ["--sidebar", "--sidebar-foreground"],
      ["--sidebar-primary", "--sidebar-primary-foreground"],
      ["--sidebar-accent", "--sidebar-accent-foreground"],
      ["--sidebar-border", "--sidebar-ring"],
    ],
  },
  {
    label: "Chart",
    tokens: [["--chart-1", "--chart-2"], ["--chart-3", "--chart-4"], ["--chart-5", ""]],
  },
];

type Reading = { value: string; runtime: boolean };

/**
 * A token is "runtime" if applyStoredThemeMode wrote it as an inline style on
 * <body>. Anything else is resolving to the static default in index.css, which
 * means no org can change it.
 */
const read = (token: string): Reading => {
  const inline = document.body.style.getPropertyValue(token).trim();
  const computed = getComputedStyle(document.body).getPropertyValue(token).trim();
  return { value: computed || "(unset)", runtime: inline !== "" };
};

const Swatch = ({ token }: { token: string }) => {
  const [r, setR] = useState<Reading>({ value: "", runtime: false });
  useEffect(() => setR(read(token)), [token]);
  if (!token) return <div />;

  return (
    <div className="flex items-center gap-3 rounded-md border border-border p-2">
      <div
        className="size-10 shrink-0 rounded-md border border-border"
        style={{ background: `var(${token})` }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-xs text-foreground">{token}</div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {r.value}
        </div>
      </div>
      <span
        className={
          "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium " +
          (r.runtime
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground")
        }
        title={
          r.runtime
            ? "Written at runtime from the org's stored theme — brandable"
            : "Falling back to the static default in index.css — NOT brandable"
        }
      >
        {r.runtime ? "org" : "static"}
      </span>
    </div>
  );
};

const ThemeTokensPage = () => {
  const [mode, setMode] = useState("");
  const [counts, setCounts] = useState({ org: 0, statik: 0 });

  useEffect(() => {
    setMode(
      document.documentElement.classList.contains("dark") ? "dark" : "light",
    );
    const all = GROUPS.flatMap((g) => g.tokens.flat()).filter(Boolean);
    let org = 0;
    all.forEach((t) => {
      if (document.body.style.getPropertyValue(t).trim() !== "") org += 1;
    });
    setCounts({ org, statik: all.length - org });
  }, []);

  return (
    <div className="min-h-screen bg-background p-6 text-foreground">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-bold">Theme token probe</h1>
          <p className="text-sm text-muted-foreground">
            Every token <code className="font-mono">tailwind.config.js</code> maps
            utilities onto, with the value currently resolving on{" "}
            <code className="font-mono">&lt;body&gt;</code>.{" "}
            <strong className="text-foreground">org</strong> = written at runtime
            from the stored theme (brandable).{" "}
            <strong className="text-foreground">static</strong> = falling back to
            index.css (not brandable).
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="rounded-md border border-border px-2 py-1">
              mode: <strong>{mode || "…"}</strong>
            </span>
            <span className="rounded-md border border-border px-2 py-1">
              org: <strong>{counts.org}</strong>
            </span>
            <span className="rounded-md border border-border px-2 py-1">
              static: <strong>{counts.statik}</strong>
            </span>
          </div>
        </header>

        {/* Live components, so the tokens are exercised and not just displayed. */}
        <section className="space-y-3 rounded-lg border border-border bg-card p-4 text-card-foreground">
          <h2 className="text-sm font-semibold">Rendered with these tokens</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground">
              Primary
            </button>
            <button className="rounded-md bg-secondary px-3 py-2 text-sm text-secondary-foreground">
              Secondary
            </button>
            <button className="rounded-md bg-destructive px-3 py-2 text-sm text-destructive-foreground">
              Destructive
            </button>
            <button className="rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground">
              Accent
            </button>
            <span className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              Muted
            </span>
            <input
              className="rounded-md border border-border bg-input px-3 py-2 text-sm"
              placeholder="Input"
            />
          </div>
          <div className="rounded-md bg-sidebar p-3 text-sidebar-foreground">
            <div className="text-xs font-semibold">Sidebar surface</div>
            <div className="mt-2 flex gap-2">
              <span className="rounded bg-sidebar-primary px-2 py-1 text-xs text-sidebar-primary-foreground">
                Active
              </span>
              <span className="rounded bg-sidebar-accent px-2 py-1 text-xs text-sidebar-accent-foreground">
                Hover
              </span>
            </div>
          </div>
        </section>

        {GROUPS.map((g) => (
          <section key={g.label} className="space-y-2">
            <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {g.label}
            </h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {g.tokens.flat().map((t, i) =>
                t ? <Swatch key={t} token={t} /> : <div key={`gap-${i}`} />,
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/theme-tokens")({
  component: ThemeTokensPage,
});
